import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import DraftControls from '../components/DraftControls';
import FieldEditor from '../components/FieldEditor';
import CustomLayoutThumb from '../components/CustomLayoutThumb';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Save, RefreshCw, ChevronDown, ChevronUp, Plus, Trash2, Edit2, X, Check,
  DollarSign, FileText, Users, Building, Mail, Phone, Eye, GripVertical,
  Globe, Award, Presentation, ArrowUp, ArrowDown, Copy, Pencil, EyeOff, Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_CMS_SECTIONS = ['hero', 'about', 'whySponsor', 'tiers', 'exhibits', 'contacts'];

const TEMPLATE_ICONS = {
  media_gallery: '🖼', document_viewer: '📄', feature_grid_diagonal: '◇',
  bento_layout: '▦', step_timeline: '⏱', layered_panels: '▧',
  layered_panels_6: '▧', layered_panels_8: '▧', custom_layout: '✦',
  speaker_preview: '🎤', sponsors: '★', program_highlights: '📋', featured_nation: '🌍',
};

const authHeaders = () => {
  const t = localStorage.getItem('admin_token');
  return t ? { 'Authorization': `Bearer ${t}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };
};

/* Sortable section wrapper */
const SortableSection = ({ id, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging, isOver } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition: transition || 'transform 200ms ease, box-shadow 200ms ease',
    opacity: isDragging ? 0.6 : 1,
    position: 'relative',
    zIndex: isDragging ? 50 : 'auto',
    scale: isDragging ? '1.01' : '1',
    boxShadow: isDragging ? '0 12px 40px rgba(0,69,127,0.15)' : 'none',
    borderRadius: '0.5rem',
  };
  return (
    <div ref={setNodeRef} style={style} data-testid={`sortable-section-${id}`}>
      {isOver && !isDragging && (
        <div className="absolute -top-1 left-4 right-4 h-0.5 bg-[#00457f] rounded-full z-20" />
      )}
      <div className="absolute left-0 top-0 bottom-0 w-8 flex items-center justify-center cursor-grab active:cursor-grabbing z-10 rounded-l-lg hover:bg-slate-100 transition-colors" {...attributes} {...listeners} data-testid={`drag-handle-${id}`}>
        <GripVertical className="w-4 h-4 text-gray-300 hover:text-gray-500" />
      </div>
      <div className="ml-6">{children}</div>
    </div>
  );
};

const AdminSponsorship = () => {
  const navigate = useNavigate();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    hero: true, about: false, whySponsor: false, tiers: false, exhibits: false, contacts: false
  });
  const [editingTier, setEditingTier] = useState(null);
  const [editingTitle, setEditingTitle] = useState(null);
  const [titleDraft, setTitleDraft] = useState('');

  // Template sections state
  const [extraSections, setExtraSections] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [showPicker, setShowPicker] = useState(false);
  const [editingExtra, setEditingExtra] = useState(null);
  const [editContent, setEditContent] = useState({});
  const [savingExtra, setSavingExtra] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Build unified section order: CMS keys + extra section IDs
  const unifiedOrder = useMemo(() => {
    if (!content) return DEFAULT_CMS_SECTIONS;
    const stored = content.section_order;
    const extraIds = extraSections.map(s => s.id);
    if (stored && stored.length > 0) {
      // Make sure all CMS sections and extra sections are in the order
      const missing = [...DEFAULT_CMS_SECTIONS, ...extraIds].filter(k => !stored.includes(k));
      return [...stored, ...missing];
    }
    // No stored order - build default: CMS sections then extras sorted by their order
    return [...DEFAULT_CMS_SECTIONS, ...extraIds];
  }, [content, extraSections]);

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = unifiedOrder.indexOf(active.id);
    const newIdx = unifiedOrder.indexOf(over.id);
    if (oldIdx === -1 || newIdx === -1) return;
    const newOrder = arrayMove(unifiedOrder, oldIdx, newIdx);
    setContent(prev => ({ ...prev, section_order: newOrder }));
  }, [unifiedOrder]);

  const isCmsSection = (key) => DEFAULT_CMS_SECTIONS.includes(key);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) { navigate('/admin/login'); return; }
    fetchAll();
  }, [navigate]);

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [cmsRes, secRes, tplRes] = await Promise.all([
        fetch(`${API_URL}/api/content/sponsorship`),
        fetch(`${API_URL}/api/homepage/sections?page=sponsorship`),
        fetch(`${API_URL}/api/homepage/templates`),
      ]);
      if (cmsRes.ok) setContent(await cmsRes.json());
      if (secRes.ok) setExtraSections(await secRes.json());
      if (tplRes.ok) setTemplates(await tplRes.json());
    } catch (err) {
      console.error('Error fetching data:', err);
      toast.error('Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  const seedContent = async () => {
    try {
      const response = await fetch(`${API_URL}/api/content/sponsorship/seed`, { method: 'POST' });
      const data = await response.json();
      toast.success(data.message);
      fetchAll();
    } catch { toast.error('Failed to seed content'); }
  };

  const saveContent = async () => {
    try {
      setSaving(true);
      const response = await fetch(`${API_URL}/api/content/sponsorship`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(content)
      });
      if (response.ok) {
        toast.success('Content saved successfully');
        fetchAll();
      } else {
        throw new Error('Failed to save');
      }
    } catch { toast.error('Failed to save content'); }
    finally { setSaving(false); }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Tier management
  const addTier = () => {
    const newTier = {
      id: `tier-${Date.now()}`, name: 'New Sponsorship Tier', price: '$0',
      description: '', benefits: [], color: '#00457f', order: content.sponsorship_tiers?.length || 0
    };
    setContent({ ...content, sponsorship_tiers: [...(content.sponsorship_tiers || []), newTier] });
    setEditingTier({ index: content.sponsorship_tiers?.length || 0, tier: newTier });
  };

  const updateTier = (index, updatedTier) => {
    const newTiers = [...content.sponsorship_tiers];
    newTiers[index] = updatedTier;
    setContent({ ...content, sponsorship_tiers: newTiers });
  };

  const deleteTier = (index) => {
    if (!window.confirm('Delete this sponsorship tier?')) return;
    setContent({ ...content, sponsorship_tiers: content.sponsorship_tiers.filter((_, i) => i !== index) });
  };

  const moveTier = (index, direction) => {
    const newTiers = [...content.sponsorship_tiers];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= newTiers.length) return;
    [newTiers[index], newTiers[newIndex]] = [newTiers[newIndex], newTiers[index]];
    setContent({ ...content, sponsorship_tiers: newTiers });
  };

  const duplicateTier = (index) => {
    const source = content.sponsorship_tiers[index];
    const clone = { ...JSON.parse(JSON.stringify(source)), id: `tier-${Date.now()}`, name: `${source.name} (Copy)` };
    const newTiers = [...content.sponsorship_tiers];
    newTiers.splice(index + 1, 0, clone);
    setContent({ ...content, sponsorship_tiers: newTiers });
    toast.success('Tier duplicated');
  };

  // Contact management
  const addContact = () => {
    setContent({ ...content, contacts: [...(content.contacts || []), { name: '', email: '', phone: '', role: '' }] });
  };
  const updateContact = (index, field, value) => {
    const newContacts = [...content.contacts];
    newContacts[index][field] = value;
    setContent({ ...content, contacts: newContacts });
  };
  const deleteContact = (index) => {
    setContent({ ...content, contacts: content.contacts.filter((_, i) => i !== index) });
  };
  const duplicateContact = (index) => {
    const source = content.contacts[index];
    const clone = { ...source, name: `${source.name} (Copy)` };
    const newContacts = [...content.contacts];
    newContacts.splice(index + 1, 0, clone);
    setContent({ ...content, contacts: newContacts });
    toast.success('Contact duplicated');
  };

  // Section title helpers
  const getSectionTitle = (key, fallback) => content?.[`section_title_${key}`] || fallback;
  const isSectionHidden = (key) => !!content?.[`section_hidden_${key}`];
  const toggleSectionVisibility = (key, e) => {
    e.stopPropagation();
    setContent(prev => ({ ...prev, [`section_hidden_${key}`]: !prev[`section_hidden_${key}`] }));
  };
  const startEditingTitle = (key, fallback, e) => {
    e.stopPropagation();
    setEditingTitle(key);
    setTitleDraft(getSectionTitle(key, fallback));
  };
  const saveTitleEdit = () => {
    if (editingTitle && titleDraft.trim()) {
      setContent({ ...content, [`section_title_${editingTitle}`]: titleDraft.trim() });
    }
    setEditingTitle(null);
  };

  // ─── Extra section operations ───
  const addExtraSection = async (sectionType, templateId) => {
    try {
      const res = await fetch(
        `${API_URL}/api/homepage/sections?section_type=${sectionType}&template_id=${templateId}&page=sponsorship`,
        { method: 'POST', headers: authHeaders() }
      );
      if (res.ok) {
        const created = await res.json();
        toast.success('Section added');
        // Refresh extra sections, then add the new ID to unified order
        const secRes = await fetch(`${API_URL}/api/homepage/sections?page=sponsorship`);
        if (secRes.ok) {
          const newSections = await secRes.json();
          setExtraSections(newSections);
          // Add new section ID to section_order
          setContent(prev => ({
            ...prev,
            section_order: [...(prev?.section_order || [...DEFAULT_CMS_SECTIONS, ...extraSections.map(s => s.id)]), created.id]
          }));
        }
        startEditingExtra(created);
      }
    } catch { toast.error('Failed to add section'); }
    setShowPicker(false);
  };

  const deleteExtraSection = async (id) => {
    if (!window.confirm('Delete this section?')) return;
    try {
      await fetch(`${API_URL}/api/homepage/sections/${id}`, { method: 'DELETE', headers: authHeaders() });
      toast.success('Section deleted');
      setExtraSections(prev => prev.filter(s => s.id !== id));
      // Remove from section_order
      setContent(prev => ({
        ...prev,
        section_order: (prev?.section_order || unifiedOrder).filter(k => k !== id)
      }));
    } catch { toast.error('Failed to delete'); }
  };

  const toggleExtraVisibility = async (id, visible) => {
    try {
      await fetch(`${API_URL}/api/homepage/sections/${id}`, {
        method: 'PUT', headers: authHeaders(), body: JSON.stringify({ visible }),
      });
      setExtraSections(prev => prev.map(s => s.id === id ? { ...s, visible } : s));
    } catch { toast.error('Failed to update'); }
  };

  const duplicateExtraSection = async (id) => {
    try {
      const res = await fetch(`${API_URL}/api/homepage/sections/${id}/duplicate`, { method: 'POST', headers: authHeaders() });
      if (res.ok) {
        const dup = await res.json();
        toast.success('Section duplicated');
        const secRes = await fetch(`${API_URL}/api/homepage/sections?page=sponsorship`);
        if (secRes.ok) {
          const newSections = await secRes.json();
          setExtraSections(newSections);
          // Insert duplicate after original in section_order
          const order = [...(content?.section_order || unifiedOrder)];
          const idx = order.indexOf(id);
          const dupId = dup.id;
          if (idx !== -1 && dupId) order.splice(idx + 1, 0, dupId);
          else if (dupId) order.push(dupId);
          setContent(prev => ({ ...prev, section_order: order }));
        }
      }
    } catch { toast.error('Failed to duplicate'); }
  };

  const startEditingExtra = (section) => {
    setEditingExtra(section);
    setEditContent(section.draft_content || section.content || {});
  };

  const saveExtraSection = async () => {
    if (!editingExtra) return;
    setSavingExtra(true);
    try {
      await fetch(`${API_URL}/api/homepage/sections/${editingExtra.id}`, {
        method: 'PUT', headers: authHeaders(), body: JSON.stringify({ content: editContent }),
      });
      toast.success('Section saved');
      setEditingExtra(null);
      setEditContent({});
      const secRes = await fetch(`${API_URL}/api/homepage/sections?page=sponsorship`);
      if (secRes.ok) setExtraSections(await secRes.json());
    } catch { toast.error('Failed to save'); }
    finally { setSavingExtra(false); }
  };

  const getTemplate = (section) => {
    const key = section.template_id || `tpl_${section.section_type}`;
    return templates.find(t => t.id === key || t.section_type === section.section_type);
  };

  const pageSpecificTypes = new Set(['hero', 'about', 'stats', 'action_cards', 'registration_cta', 'location']);
  const availableTemplates = templates.filter(t => !pageSpecificTypes.has(t.section_type));

  // Section Header renderer
  const renderSectionHeader = (title, section, Icon, badge, titleKey, defaultTitle) => {
    const displayTitle = titleKey ? getSectionTitle(titleKey, defaultTitle || title) : title;
    const isEditing = editingTitle === titleKey;
    const hidden = titleKey ? isSectionHidden(titleKey) : false;

    return (
      <div className={`w-full flex items-center justify-between p-4 transition-colors rounded-t-lg ${hidden ? 'bg-slate-100/60' : 'bg-slate-50 hover:bg-slate-100'}`}>
        <button onClick={() => toggleSection(section)} className="flex items-center gap-3 flex-1 min-w-0 text-left">
          <Icon className={`w-5 h-5 shrink-0 ${hidden ? 'text-gray-300' : 'text-[#00457f]'}`} />
          {isEditing ? (
            <form className="flex items-center gap-2 flex-1" onSubmit={(e) => { e.preventDefault(); saveTitleEdit(); }} onClick={(e) => e.stopPropagation()}>
              <input autoFocus value={titleDraft} onChange={(e) => setTitleDraft(e.target.value)} onBlur={saveTitleEdit}
                className="flex-1 px-2 py-1 text-sm font-semibold border rounded-md bg-white outline-none focus:ring-2 focus:ring-[#00457f]"
                data-testid={`edit-title-input-${titleKey}`} />
            </form>
          ) : (
            <span className={`font-semibold truncate ${hidden ? 'text-gray-400 line-through' : 'text-gray-900'}`}>{displayTitle}</span>
          )}
          {hidden && <span className="px-1.5 py-0.5 bg-gray-200 text-gray-500 text-[10px] font-bold rounded uppercase tracking-wider shrink-0">Hidden</span>}
          {badge !== undefined && <span className="px-2 py-0.5 bg-[#00457f] text-white text-xs rounded-full shrink-0">{badge}</span>}
        </button>
        <div className="flex items-center gap-1 shrink-0">
          {titleKey && (
            <button onClick={(e) => toggleSectionVisibility(titleKey, e)}
              className={`p-1.5 rounded-lg transition-colors ${hidden ? 'text-red-400 hover:text-red-600 hover:bg-red-50' : 'text-green-500 hover:text-green-700 hover:bg-green-50'}`}
              title={hidden ? 'Show on public page' : 'Hide from public page'} data-testid={`toggle-vis-${titleKey}`}>
              {hidden ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
            </button>
          )}
          {titleKey && !isEditing && (
            <button onClick={(e) => startEditingTitle(titleKey, defaultTitle || title, e)}
              className="p-1.5 text-gray-400 hover:text-[#00457f] hover:bg-blue-50 rounded-lg transition-colors"
              title="Edit section title" data-testid={`edit-title-btn-${titleKey}`}>
              <Pencil className="w-3.5 h-3.5" />
            </button>
          )}
          <button onClick={() => toggleSection(section)} className="p-1">
            {expandedSections[section] ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
          </button>
        </div>
      </div>
    );
  };

  // CMS section content renderers
  const renderCmsSection = (key) => {
    switch (key) {
      case 'hero':
        return (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            {renderSectionHeader("Hero Section", "hero", FileText)}
            {expandedSections.hero && (
              <div className="p-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Eyebrow Text</label>
                  <input type="text" value={content.hero_eyebrow || ''} onChange={(e) => setContent({ ...content, hero_eyebrow: e.target.value })} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#00457f] focus:border-transparent" placeholder="e.g. World Nuclear Congress 2027" />
                  <p className="text-xs text-gray-500 mt-1">Small text above the title</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Page Title</label>
                  <input type="text" value={content.hero_title || ''} onChange={(e) => setContent({ ...content, hero_title: e.target.value })} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#00457f] focus:border-transparent" placeholder="Sponsorship and Exhibit Showcase" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Subtitle</label>
                  <textarea value={content.hero_subtitle || ''} onChange={(e) => setContent({ ...content, hero_subtitle: e.target.value })} rows={2} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#00457f] focus:border-transparent" placeholder="e.g. The Independent U.S. Forum for Global Nuclear Progress" />
                  <p className="text-xs text-gray-500 mt-1">Text below the title</p>
                </div>
              </div>
            )}
          </div>
        );
      case 'about':
        return (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            {renderSectionHeader("About the World Nuclear Congress", "about", Globe, undefined, "about", "About the World Nuclear Congress")}
            {expandedSections.about && (
              <div className="p-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Eyebrow Text</label>
                  <input type="text" value={content.about_eyebrow || ''} onChange={(e) => setContent({ ...content, about_eyebrow: e.target.value })} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#00457f] focus:border-transparent" placeholder="e.g. Our Mission" />
                  <p className="text-xs text-gray-500 mt-1">Small text above the section title</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Subtitle</label>
                  <input type="text" value={content.about_subtitle || ''} onChange={(e) => setContent({ ...content, about_subtitle: e.target.value })} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#00457f] focus:border-transparent" placeholder="e.g. An independent forum for nuclear progress" />
                  <p className="text-xs text-gray-500 mt-1">Text below the section title</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">About Text</label>
                  <textarea value={content.about_text || ''} onChange={(e) => setContent({ ...content, about_text: e.target.value })} rows={6} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#00457f] focus:border-transparent" placeholder="Describe the World Nuclear Congress..." />
                </div>
              </div>
            )}
          </div>
        );
      case 'whySponsor':
        return (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            {renderSectionHeader("Why Sponsor Section", "whySponsor", Award, undefined, "whySponsor", "Why Sponsor Section")}
            {expandedSections.whySponsor && (
              <div className="p-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Eyebrow Text</label>
                  <input type="text" value={content.why_sponsor_eyebrow || ''} onChange={(e) => setContent({ ...content, why_sponsor_eyebrow: e.target.value })} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#00457f] focus:border-transparent" placeholder="e.g. Partnership Opportunity" />
                  <p className="text-xs text-gray-500 mt-1">Small text above the section title</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Subtitle</label>
                  <input type="text" value={content.why_sponsor_subtitle || ''} onChange={(e) => setContent({ ...content, why_sponsor_subtitle: e.target.value })} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#00457f] focus:border-transparent" placeholder="e.g. Meaningful visibility within a trusted global forum" />
                  <p className="text-xs text-gray-500 mt-1">Text below the section title</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Introduction Text</label>
                  <textarea value={content.why_sponsor_intro || ''} onChange={(e) => setContent({ ...content, why_sponsor_intro: e.target.value })} rows={4} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#00457f] focus:border-transparent" placeholder="Why organizations should sponsor..." />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Key Benefits (one per line)</label>
                  <textarea value={content.why_sponsor_points?.join('\n') || ''} onChange={(e) => setContent({ ...content, why_sponsor_points: e.target.value.split('\n').filter(Boolean) })} rows={6} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#00457f] focus:border-transparent" placeholder="Enter each benefit on a new line..." />
                  <p className="text-xs text-gray-500 mt-1">These appear as bullet points with icons</p>
                </div>
              </div>
            )}
          </div>
        );
      case 'tiers':
        return (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            {renderSectionHeader("Sponsorship Tiers", "tiers", DollarSign, content.sponsorship_tiers?.length || 0, "tiers", "Sponsorship Tiers")}
            {expandedSections.tiers && (
              <div className="p-4 space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 p-3 bg-slate-50 rounded-lg">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Eyebrow Text</label>
                    <input type="text" value={content.tiers_eyebrow || ''} onChange={(e) => setContent({ ...content, tiers_eyebrow: e.target.value })} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#00457f] focus:border-transparent" placeholder="e.g. Invest in Impact" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Subtitle</label>
                    <input type="text" value={content.tiers_subtitle || ''} onChange={(e) => setContent({ ...content, tiers_subtitle: e.target.value })} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#00457f] focus:border-transparent" placeholder="e.g. Choose the level that fits your goals" />
                  </div>
                </div>
                {content.sponsorship_tiers?.map((tier, index) => (
                  <div key={tier.id || index} className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg border-l-4 group" style={{ borderLeftColor: tier.color || '#00457f' }}>
                    <div className="flex flex-col gap-1 pt-1">
                      <button onClick={() => moveTier(index, 'up')} disabled={index === 0} className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"><ArrowUp className="w-4 h-4" /></button>
                      <button onClick={() => moveTier(index, 'down')} disabled={index === content.sponsorship_tiers.length - 1} className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"><ArrowDown className="w-4 h-4" /></button>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-1">
                        <h4 className="font-bold text-gray-900 truncate">{tier.name}</h4>
                        <span className="px-2 py-0.5 bg-green-100 text-green-700 text-sm font-bold rounded">{tier.price}</span>
                      </div>
                      <p className="text-sm text-gray-600 line-clamp-2">{tier.description}</p>
                      {tier.benefits?.length > 0 && <p className="text-xs text-gray-400 mt-1">{tier.benefits.length} benefit{tier.benefits.length !== 1 ? 's' : ''} listed</p>}
                    </div>
                    <div className="flex gap-1">
                      <button onClick={() => duplicateTier(index)} className="p-2 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors" title="Duplicate" data-testid={`duplicate-tier-${index}`}><Copy className="w-4 h-4" /></button>
                      <button onClick={() => setEditingTier({ index, tier: { ...tier } })} className="p-2 text-gray-400 hover:text-[#00457f] hover:bg-blue-50 rounded-lg transition-colors"><Edit2 className="w-4 h-4" /></button>
                      <button onClick={() => deleteTier(index)} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={addTier} className="mt-2"><Plus className="w-4 h-4 mr-2" />Add Sponsorship Tier</Button>
              </div>
            )}
          </div>
        );
      case 'exhibits':
        return (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            {renderSectionHeader("Exhibit Showcase Options", "exhibits", Building, undefined, "exhibits", "Exhibit Showcase Options")}
            {expandedSections.exhibits && (
              <div className="p-4 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-3 bg-slate-50 rounded-lg">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Eyebrow Text</label>
                    <input type="text" value={content.exhibits_eyebrow || ''} onChange={(e) => setContent({ ...content, exhibits_eyebrow: e.target.value })} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#00457f] focus:border-transparent" placeholder="e.g. Showcase Your Organization" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Subtitle</label>
                    <input type="text" value={content.exhibits_subtitle || ''} onChange={(e) => setContent({ ...content, exhibits_subtitle: e.target.value })} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#00457f] focus:border-transparent" placeholder="e.g. Premium exhibit spaces for maximum visibility" />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Price</label>
                    <input type="text" value={content.exhibit_info?.price || ''} onChange={(e) => setContent({ ...content, exhibit_info: { ...content.exhibit_info, price: e.target.value } })} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#00457f]" placeholder="$3,500" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea value={content.exhibit_info?.description || ''} onChange={(e) => setContent({ ...content, exhibit_info: { ...content.exhibit_info, description: e.target.value } })} rows={4} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#00457f]" placeholder="Describe the exhibit showcase opportunity..." />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">What&apos;s Included (one per line)</label>
                    <textarea value={content.exhibit_info?.includes?.join('\n') || ''} onChange={(e) => setContent({ ...content, exhibit_info: { ...content.exhibit_info, includes: e.target.value.split('\n').filter(Boolean) } })} rows={6} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#00457f]" placeholder="Each included item on a new line..." />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Why Participate (one per line)</label>
                    <textarea value={content.exhibit_info?.why_participate?.join('\n') || ''} onChange={(e) => setContent({ ...content, exhibit_info: { ...content.exhibit_info, why_participate: e.target.value.split('\n').filter(Boolean) } })} rows={6} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#00457f]" placeholder="Each reason on a new line..." />
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      case 'contacts':
        return (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            {renderSectionHeader("Contact Information", "contacts", Mail, content.contacts?.length || 0, "contacts", "Contact Information")}
            {expandedSections.contacts && (
              <div className="p-4 space-y-3">
                {content.contacts?.map((contact, index) => (
                  <div key={index} className="p-4 bg-gray-50 rounded-lg">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                      <div><label className="block text-xs font-medium text-gray-500 mb-1">Name</label><input type="text" value={contact.name} onChange={(e) => updateContact(index, 'name', e.target.value)} placeholder="Contact Name" className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#00457f] text-sm" /></div>
                      <div><label className="block text-xs font-medium text-gray-500 mb-1">Email</label><input type="email" value={contact.email} onChange={(e) => updateContact(index, 'email', e.target.value)} placeholder="email@example.com" className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#00457f] text-sm" /></div>
                      <div><label className="block text-xs font-medium text-gray-500 mb-1">Phone</label><input type="tel" value={contact.phone || ''} onChange={(e) => updateContact(index, 'phone', e.target.value)} placeholder="+1 555-555-5555" className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#00457f] text-sm" /></div>
                      <div className="flex gap-2">
                        <div className="flex-1"><label className="block text-xs font-medium text-gray-500 mb-1">Role</label><input type="text" value={contact.role || ''} onChange={(e) => updateContact(index, 'role', e.target.value)} placeholder="Sponsorship Inquiries" className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#00457f] text-sm" /></div>
                        <button onClick={() => duplicateContact(index)} className="mt-5 p-2 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg" title="Duplicate" data-testid={`duplicate-contact-${index}`}><Copy className="w-4 h-4" /></button>
                        <button onClick={() => deleteContact(index)} className="mt-5 p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </div>
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={addContact}><Plus className="w-4 h-4 mr-2" />Add Contact</Button>
              </div>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  // Extra template section card (compact)
  const renderExtraSection = (sectionId) => {
    const section = extraSections.find(s => s.id === sectionId);
    if (!section) return null;
    return (
      <div className={`bg-white border rounded-lg overflow-hidden shadow transition-all ${!section.visible ? 'opacity-60' : ''}`} data-testid={`extra-section-card-${section.id}`}>
        <div className="flex items-center gap-4 p-4">
          <div className="w-10 h-10 bg-indigo-50 rounded-lg flex items-center justify-center text-lg shrink-0">
            {TEMPLATE_ICONS[section.section_type] || '▧'}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-0.5">
              <h3 className="font-semibold text-gray-900 truncate">{section.name}</h3>
              <span className="px-1.5 py-0.5 bg-indigo-100 text-indigo-700 text-[10px] font-bold rounded uppercase tracking-wider shrink-0">Template</span>
              {!section.visible && <span className="px-1.5 py-0.5 bg-gray-200 text-gray-500 text-[10px] font-bold rounded uppercase tracking-wider shrink-0">Hidden</span>}
            </div>
            <p className="text-xs text-gray-500">Type: {section.section_type}</p>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button onClick={() => toggleExtraVisibility(section.id, !section.visible)}
              className={`p-1.5 rounded-lg transition-colors ${section.visible ? 'text-green-500 hover:text-green-700 hover:bg-green-50' : 'text-red-400 hover:text-red-600 hover:bg-red-50'}`}
              title={section.visible ? 'Hide' : 'Show'} data-testid={`toggle-extra-vis-${section.id}`}>
              {section.visible ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
            </button>
            <button onClick={() => startEditingExtra(section)} className="p-1.5 text-gray-400 hover:text-[#00457f] hover:bg-blue-50 rounded-lg transition-colors" title="Edit">
              <Edit2 className="w-3.5 h-3.5" />
            </button>
            <button onClick={() => duplicateExtraSection(section.id)} className="p-1.5 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors" title="Duplicate">
              <Copy className="w-3.5 h-3.5" />
            </button>
            <button onClick={() => deleteExtraSection(section.id)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Delete">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex justify-center items-center h-64">
          <div className="w-12 h-12 border-4 border-[#00457f] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6 max-w-5xl mx-auto">
        {/* Draft & Publish Controls */}
        <div className="mb-4">
          <DraftControls draftKey="sponsorship" pageLabel="Sponsorship" previewPath="/sponsorship" />
        </div>
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Sponsorship Page Manager</h1>
            <p className="text-gray-600">Drag sections to reorder the entire page layout</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => setShowPicker(true)} variant="outline" size="sm" className="flex items-center gap-2" data-testid="add-section-btn">
              <Plus className="w-4 h-4" />
              Add Section
            </Button>
            <Button onClick={seedContent} variant="outline" size="sm" className="flex items-center gap-2">
              <RefreshCw className="w-4 h-4" />
              Seed Default Data
            </Button>
            <Button onClick={saveContent} disabled={saving} className="bg-[#00457f] hover:bg-[#003461] flex items-center gap-2" data-testid="save-all-btn">
              {saving ? <><RefreshCw className="w-4 h-4 animate-spin" />Saving...</> : <><Save className="w-4 h-4" />Save All Changes</>}
            </Button>
          </div>
        </div>

        {content && (
          <div className="space-y-4">
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={unifiedOrder} strategy={verticalListSortingStrategy}>
                {unifiedOrder.map((key) => {
                  const jsx = isCmsSection(key) ? renderCmsSection(key) : renderExtraSection(key);
                  if (!jsx) return null;
                  return (
                    <SortableSection key={key} id={key}>
                      {jsx}
                    </SortableSection>
                  );
                })}
              </SortableContext>
            </DndContext>

            {/* Last Updated */}
            {content.updated_at && (
              <p className="text-sm text-gray-500 text-center">
                Last updated: {new Date(content.updated_at).toLocaleString()}
              </p>
            )}
          </div>
        )}

        {/* Tier Edit Modal */}
        {editingTier && (
          <TierEditModal
            tier={editingTier.tier}
            onSave={(updatedTier) => { updateTier(editingTier.index, updatedTier); setEditingTier(null); }}
            onClose={() => setEditingTier(null)}
          />
        )}

        {/* Template picker modal */}
        {showPicker && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowPicker(false)}>
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-y-auto m-4" onClick={(e) => e.stopPropagation()} data-testid="template-picker">
              <div className="sticky top-0 bg-white border-b px-5 py-4 flex items-center justify-between z-10">
                <h3 className="font-bold text-lg">Add Section</h3>
                <button onClick={() => setShowPicker(false)} className="p-1 rounded hover:bg-slate-100"><X className="w-5 h-5" /></button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 p-5">
                {availableTemplates.map((tpl) => {
                  const isCustom = tpl.is_custom && tpl.layout_preview;
                  return (
                    <button key={tpl.id} onClick={() => addExtraSection(tpl.section_type, tpl.id)}
                      className="border rounded-xl p-3 text-left hover:border-[#00457f] hover:shadow-md transition-all group"
                      data-testid={`tpl-pick-${tpl.section_type}`}>
                      <div className="h-16 bg-slate-50 rounded-lg mb-2 flex items-center justify-center text-2xl text-slate-400 overflow-hidden">
                        {isCustom ? <CustomLayoutThumb layout={tpl.layout_preview} /> : <span>{TEMPLATE_ICONS[tpl.section_type] || '▧'}</span>}
                      </div>
                      <p className="text-sm font-semibold text-slate-800 truncate group-hover:text-[#00457f]">{tpl.name}</p>
                      <p className="text-xs text-slate-500 line-clamp-2 mt-0.5">{tpl.description}</p>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Extra section editor slide-out */}
        {editingExtra && (
          <div className="fixed inset-0 bg-black/50 flex justify-end z-50">
            <div className="w-full max-w-lg bg-white h-full flex flex-col overflow-hidden" data-testid="extra-section-editor">
              <div className="sticky top-0 bg-white border-b p-4 flex items-center justify-between z-10">
                <div>
                  <h2 className="font-bold text-lg">{editingExtra.name}</h2>
                  <p className="text-sm text-gray-500">{editingExtra.section_type}</p>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => { setEditingExtra(null); setEditContent({}); }}>Cancel</Button>
                  <Button onClick={saveExtraSection} disabled={savingExtra} data-testid="save-extra-section-btn">
                    {savingExtra ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
                    Save
                  </Button>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-5 space-y-5">
                {(() => {
                  const tpl = getTemplate(editingExtra);
                  return tpl?.fields?.map((field) => (
                    <FieldEditor key={field.name} field={field} value={editContent[field.name]}
                      onChange={(name, val) => setEditContent(prev => ({ ...prev, [name]: val }))} />
                  ));
                })()}
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

// Tier Edit Modal
const TierEditModal = ({ tier, onSave, onClose }) => {
  const [editedTier, setEditedTier] = useState(tier);
  const colorOptions = [
    { name: 'Gold', value: '#D4AF37' }, { name: 'Navy', value: '#00457f' },
    { name: 'Green', value: '#2D5016' }, { name: 'Purple', value: '#5B2C6F' },
    { name: 'Teal', value: '#1A5276' }, { name: 'Red', value: '#922B21' }
  ];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-[#00457f]">Edit Sponsorship Tier</h2>
            <button onClick={onClose} className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
          </div>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tier Name *</label>
                <input type="text" value={editedTier.name} onChange={(e) => setEditedTier({ ...editedTier, name: e.target.value })} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#00457f]" placeholder="e.g., Founding Institutional Sponsor" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Price *</label>
                <input type="text" value={editedTier.price} onChange={(e) => setEditedTier({ ...editedTier, price: e.target.value })} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#00457f]" placeholder="e.g., $15,000" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea value={editedTier.description} onChange={(e) => setEditedTier({ ...editedTier, description: e.target.value })} rows={3} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#00457f]" placeholder="Describe this sponsorship tier..." />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Benefits (one per line)</label>
              <textarea value={editedTier.benefits?.join('\n') || ''} onChange={(e) => setEditedTier({ ...editedTier, benefits: e.target.value.split('\n').filter(Boolean) })} rows={6} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#00457f]" placeholder="Each benefit on a new line..." />
              <p className="text-xs text-gray-500 mt-1">{editedTier.benefits?.length || 0} benefits listed</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Accent Color</label>
              <div className="flex flex-wrap gap-2">
                {colorOptions.map(color => (
                  <button key={color.value} onClick={() => setEditedTier({ ...editedTier, color: color.value })}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border-2 transition-all ${editedTier.color === color.value ? 'border-gray-900 shadow-md' : 'border-gray-200 hover:border-gray-300'}`}>
                    <div className="w-5 h-5 rounded-full" style={{ backgroundColor: color.value }} />
                    <span className="text-sm">{color.name}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={() => onSave(editedTier)} className="bg-[#00457f] hover:bg-[#003461]">
              <Check className="w-4 h-4 mr-2" />Save Tier
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSponsorship;
